import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight } from "lucide-react";
import { DOMAINS, type Domain } from "@/lib/design-system";

interface PageHeaderProps {
  title: string;
  description?: string;
  domain: Domain;
  badge?: string;
  actions?: ReactNode;
  href?: string;
  children?: ReactNode; // KPI row
}

/**
 * Phase 3 — STANDARD PAGE TEMPLATE
 *
 * Every page must have:
 *   - Header (title, description, domain badge, actions)
 *   - KPI Row (max 4 KPIs, never more)
 *   - Filters (below KPIs)
 *   - Main content (Table / Timeline / Kanban / Map / Chart)
 *
 * This component enforces the header pattern across all centers.
 */
export function PageHeader({ title, description, domain, badge, actions, children }: PageHeaderProps) {
  const d = DOMAINS[domain];
  return (
    <header className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className={d.badge}>{d.short}</Badge>
            <span className="text-xs text-slate-500">· {d.purpose}</span>
            {badge && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {description && <p className="text-sm text-slate-400 mt-1">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/**
 * 4 KPI cards maximum per page. Trend delta + sparkline.
 */
interface KpiBlockProps {
  items: Array<{
    label: string;
    value: number | string;
    unit?: string;
    delta?: number;
    sparkline?: number[];
    intent?: "good" | "bad" | "neutral";
    icon?: any;
  }>;
  domain: Domain;
  href?: string;
}

export function KpiRow({ items, domain, href }: KpiBlockProps) {
  const d = (DOMAINS[domain] || DOMAINS.neutral) as typeof DOMAINS[Domain];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {(items || []).map((k, i) => {
        const Icon = k.icon;
        return (
          <Card key={i} className={`bg-slate-900/60 border-slate-800 hover:${d.borderClass} transition`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-1">
                <div className={`h-8 w-8 rounded-lg ${d.bgClass} ${d.textClass} flex items-center justify-center`}>
                  {Icon ? <Icon className="h-4 w-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                </div>
                {k.delta !== undefined && (
                  <Badge variant="outline" className={`text-[10px] ${
                    k.intent === "bad" ? "border-red-500/30 text-red-400" :
                    k.delta > 0 ? "border-emerald-500/30 text-emerald-400" :
                    k.delta < 0 ? "border-amber-500/30 text-amber-400" :
                    "border-slate-500/30 text-slate-400"
                  }`}>
                    {k.delta > 0 ? "+" : ""}{k.delta}%
                  </Badge>
                )}
              </div>
              <div className="text-2xl font-bold tabular-nums">{k.value}{k.unit && <span className="text-sm font-normal text-slate-500 ml-1">{k.unit}</span>}</div>
              <div className="text-xs text-slate-400 mt-1">{k.label}</div>
              {k.sparkline && <Sparkline data={k.sparkline} color={d.color} />}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length === 0) return null;
  const w = 100, h = 20;
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="mt-2 w-full" preserveAspectRatio="none" viewBox={`0 0 ${w} ${h}`}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      <polyline fill={`${color}22`} stroke="none" points={`0,${h} ${points} ${w},${h}`} />
    </svg>
  );
}

export function FiltersBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 flex-wrap bg-slate-900/40 border border-slate-800 rounded-lg p-3">
      {children}
    </div>
  );
}

export function CenterGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">{children}</div>;
}